import { lazy } from 'react';

// Lazy-loaded components
const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
const CVE = lazy(() => import('../pages/vulnerability/cve'));
const RemediationVulnerability = lazy(
  () => import('../pages/remediationVulnerability')
);
const RemediationVulnerabilityCreate = lazy(
  () => import('../pages/remediationVulnerability/create')
);
const RemediationVulnerabilityDetail = lazy(
  () => import('../pages/remediationVulnerability/detail')
);
const RemediationAssets = lazy(() => import('../pages/remediationAssets'));
const RemediationAssetDetail = lazy(
  () => import('../pages/remediationAssets/detail')
);
const RemediationApplication = lazy(
  () => import('../pages/remediationApplication/index')
);
const RemediationApplicationCreate = lazy(
  () => import('../pages/remediationApplication/create/index')
);
const RemediationApplicationDashboard = lazy(
  () => import('../pages/remediationApplication/dashboard/index')
);
const RemediationApplicationDetail = lazy(
  () => import('../pages/remediationApplication/detail/index')
);
const RemediationReports = lazy(() => import('../pages/remediationReports'));
const RemediationProjects = lazy(() => import('../pages/remediationProjects'));
const RemediationProjectDetail = lazy(
  () => import('../pages/remediationProjects/detail')
);
const RemediationProjectCreate = lazy(
  () => import('../pages/remediationProjects/create')
);
const SettingPage = lazy(() => import('../pages/settings'));
const ConnectorsPage = lazy(() => import('../pages/connectors'));
const ImportCenterPage = lazy(() => import('../pages/imports'));
const UnmappedFindingsPage = lazy(() => import('../pages/imports/UnmappedFindings'));
const QualityDiagnostics = lazy(() => import('../pages/security/QualityDiagnostics'));
const AcunetixConnectorPage = lazy(
  () => import('../pages/connectors/acunetix/connector')
);
const AcunetixDashboardPage = lazy(
  () => import('../pages/connectors/acunetix/dashboard')
);
const AcunetixScanPage = lazy(
  () => import('../pages/connectors/acunetix/scan')
);
const CodeRepositories = lazy(() => import('../pages/codeRepositories'));
const CodeRepositoryDetail = lazy(() => import('../pages/codeRepositories/detail'));
const JiraConnectPage = lazy(() => import('../pages/connectors/jira'));
const SeewardSocConnectorPage = lazy(
  () => import('../pages/connectors/seeward-soc')
);
const NessusConnectorPage = lazy(() => import('../pages/connectors/nessus'));
const OrgConsole = lazy(() => import('../pages/org/OrgConsole'));
const EntityConsole = lazy(() => import('../pages/entity/EntityConsole'));
const WorkspaceAccess = lazy(() => import('../pages/access/WorkspaceAccess'));
const NotFoundPage = lazy(() => import('../pages/404/404Page'));

// Non-lazy components (assuming these are not lazy-loaded in your original setup)
import User from '../pages/user/User';
import CreateUserPage from '../pages/user/create/CreateUser';

const SYS_ADMIN = 'SYS_ADMIN';

const coreRoutes = [
  {
    path: '/security/code/repositories',
    title: 'Repositories',
    breadcrumb: 'Repositories',
    component: CodeRepositories,
    requiredRoles: []
  }, {
    path: '/security/code/repositories/:id',
    title: 'Repository',
    breadcrumb: 'Repository',
    component: CodeRepositoryDetail,
    requiredRoles: []
  }, {
    path: '/security/code/findings',
    title: 'Code Findings',
    breadcrumb: 'Findings',
    component: RemediationVulnerability,
    requiredRoles: []
  }, {
    path: '/security/code/quality',
    title: 'Quality Diagnostics',
    breadcrumb: 'Quality Diagnostics',
    component: QualityDiagnostics,
    requiredRoles: []
  }, {
    path: '/security/infrastructure/assets',
    title: 'Infrastructure Assets',
    breadcrumb: 'Assets',
    component: RemediationAssets,
    requiredRoles: []
  }, {
    path: '/security/infrastructure/findings',
    title: 'Infrastructure Findings',
    breadcrumb: 'Findings',
    component: RemediationVulnerability,
    requiredRoles: []
  }, {
    path: '/',
    title: 'Dashboard',
    breadcrumb: 'Dashboard',
    component: Dashboard,
    requiredRoles: []
  }, {
    path: '/security/infrastructure/findings/create',
    title: 'Create Finding',
    breadcrumb: 'Create Finding',
    component: RemediationVulnerabilityCreate,
    requiredRoles: []
  }, {
    path: '/security/infrastructure/findings/:id',
    title: 'Finding Detail',
    breadcrumb: 'Finding Detail',
    component: RemediationVulnerabilityDetail,
    requiredRoles: []
  }, {
    path: '/security/code/findings/:id',
    title: 'Finding Detail',
    breadcrumb: 'Finding Detail',
    component: RemediationVulnerabilityDetail,
    requiredRoles: []
  }, {
    path: '/security/infrastructure/assets/:id',
    title: 'Asset Detail',
    breadcrumb: 'Asset Detail',
    component: RemediationAssetDetail,
    requiredRoles: []
  }, {
    path: '/remediation/applications',
    title: 'Remediation Applications',
    breadcrumb: 'Remediation Applications',
    component: RemediationApplication,
    requiredRoles: []
  }, {
    path: '/remediation/applications/new',
    title: 'Create Remediation Application',
    breadcrumb: 'Create Remediation Application',
    component: RemediationApplicationCreate,
    requiredRoles: []
  }, {
    path: '/remediation/applications/:id',
    title: 'Remediation Application',
    breadcrumb: 'Remediation Application',
    component: RemediationApplicationDashboard,
    requiredRoles: []
  }, {
    path: '/remediation/applications/:id/edit',
    title: 'Remediation Application Detail',
    breadcrumb: 'Remediation Application Detail',
    component: RemediationApplicationDetail,
    requiredRoles: []
  }, {
    path: '/remediation/reports',
    title: 'Remediation Reports',
    breadcrumb: 'Remediation Reports',
    component: RemediationReports,
    requiredRoles: []
  }, {
    path: '/remediation/projects',
    title: 'Remediation Projects',
    breadcrumb: 'Remediation Projects',
    component: RemediationProjects,
    requiredRoles: []
  }, {
    path: '/remediation/projects/create',
    title: 'Create Remediation Project',
    breadcrumb: 'Create Remediation Project',
    component: RemediationProjectCreate,
    requiredRoles: []
  }, {
    path: '/remediation/projects/:id',
    title: 'Remediation Project Detail',
    breadcrumb: 'Remediation Project Detail',
    component: RemediationProjectDetail,
    requiredRoles: []
  }, {
    path: '/vulnerability/cve',
    title: 'CVE',
    breadcrumb: 'CVE',
    component: CVE,
    requiredRoles: []
  }, {
    path: '/settings/*',
    title: 'Settings',
    breadcrumb: 'Settings',
    component: SettingPage,
    requiredRoles: []
  }, {
    path: '/connectors/*', // Updated to support nested routes
    title: 'Connectors',
    breadcrumb: 'Connectors',
    component: ConnectorsPage,
    requiredRoles: []
  }, {
    path: '/imports',
    title: 'Import Center',
    breadcrumb: 'Import Center',
    component: ImportCenterPage,
    requiredRoles: []
  }, {
    path: '/imports/unmapped',
    title: 'Unmapped Findings',
    breadcrumb: 'Unmapped Findings',
    component: UnmappedFindingsPage,
    requiredRoles: []
  }, {
    path: '/connectors/jira',
    title: 'Jira Connector',
    breadcrumb: 'Jira Connector',
    component: JiraConnectPage,
    requiredRoles: []
  }, {
    path: '/connectors/seeward-soc',
    title: 'Seeward SOC Connector',
    breadcrumb: 'Seeward SOC Connector',
    component: SeewardSocConnectorPage,
    requiredRoles: []
  }, {
    path: '/connectors/nessus',
    title: 'Nessus Connector',
    breadcrumb: 'Nessus Connector',
    component: NessusConnectorPage,
    requiredRoles: []
  }, {
    path: '/connectors/acunetix',
    title: 'Acunetix Connector',
    breadcrumb: 'Acunetix Connector',
    component: AcunetixConnectorPage,
    requiredRoles: []
  }, {
    path: '/connectors/acunetix/dashboard',
    title: 'Acunetix Dashboard',
    breadcrumb: 'Acunetix Dashboard',
    component: AcunetixDashboardPage,
    requiredRoles: []
  }, {
    path: '/connectors/acunetix/scan',
    title: 'Acunetix Scan',
    breadcrumb: 'Acunetix Scan',
    component: AcunetixScanPage,
    requiredRoles: []
  }, {
    path: '/user',
    title: 'User',
    breadcrumb: 'User',
    component: User,
    requiredRoles: [SYS_ADMIN]
  }, {
    path: '/user/create',
    title: 'Create User',
    breadcrumb: 'Create User',
    component: CreateUserPage,
    requiredRoles: [SYS_ADMIN]
  }, {
    path: '/access',
    title: 'Access',
    breadcrumb: 'Access',
    component: WorkspaceAccess,
    requiredRoles: []
  }, {
    path: '/org/:orgId/*',
    title: 'Organisation',
    breadcrumb: 'Organisation',
    component: OrgConsole,
    requiredRoles: []
  }, {
    path: '/entity/:entityId/*',
    title: 'Entity',
    breadcrumb: 'Entity',
    component: EntityConsole,
    requiredRoles: []
  }, {
    path: '*',
    title: 'Not Found',
    breadcrumb: 'Not Found',
    component: NotFoundPage,
    requiredRoles: []
  }
];

const routes = [...coreRoutes];
export default routes;
